/* eslint-disable @typescript-eslint/no-explicit-any */
/*
Context Detection Engine for Next.js Chat Widget (TypeScript)

File: context-detection-engine.ts
Purpose: Analyze URL, metadata, and DOM to detect page type and extract structured context

Features implemented:
- Detect page type (expert, service, industry) via URL regex and DOM selectors
- Extract metadata: title, description, keywords, og tags
- Extract relevant keywords from URL segments and key DOM elements (h1, h2, meta keywords)
- Caching in localStorage with TTL
- Modular: add new page types at runtime
- Timeout handling (process within 500ms by default)
- Error handling and analytics hooks

Usage:
import ContextDetector from './context-detection-engine';

const detector = new ContextDetector({analytics, cacheTTL: 5 * 60 * 1000});
const context = await detector.detect({url: window.location.href, doc: document});
// use `context` to prefill chat

New options:
- baseOrigin?: string                 // SSR-safe URL base (fallback used if window is not available)
- storage?: Storage | null            // Injectable storage (default localStorage when available)
- disableCache?: boolean              // Skip reading/writing cache when true
*/

import { ContextEngine } from '@/types';
import { matchByUrl } from './core/matchers';
import { extractKeywords, extractMetadata } from './core/extractors';
import {
  clearCache as cacheClear,
  readCache as cacheRead,
  writeCache as cacheWrite,
} from './core/cache';

// -----------------------------
// Utility helpers
// -----------------------------
const DEFAULT_TIMEOUT_MS = 500;
const DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function timeoutPromise(ms: number): Promise<never> {
  return new Promise((_, rej) =>
    setTimeout(() => rej(new Error('context_detection_timeout')), ms)
  );
}

// default page types are imported from './config/page-types'

// -----------------------------
// ContextDetector class
// -----------------------------
export default class ContextDetector {
  private pageTypes: Record<string, ContextEngine.PageTypeConfig>;
  private cacheTTL: number;
  private analytics: ContextEngine.ContextDetectorOptions['analytics'];
  private timeoutMs: number;
  private storage: Storage | null;
  private baseOrigin?: string;
  private disableCache: boolean;

  constructor(options: ContextEngine.ContextDetectorOptions = {}) {
    this.pageTypes = options.pageTypes || {};
    this.cacheTTL =
      typeof options.cacheTTL === 'number'
        ? options.cacheTTL
        : DEFAULT_CACHE_TTL;
    this.analytics = options.analytics || null;
    this.timeoutMs =
      typeof options.timeoutMs === 'number'
        ? options.timeoutMs
        : DEFAULT_TIMEOUT_MS;
    this.baseOrigin = options.baseOrigin;
    this.storage =
      typeof options.storage !== 'undefined'
        ? options.storage
        : typeof window !== 'undefined'
          ? window.localStorage
          : null;
    this.disableCache = Boolean(options.disableCache);
  }

  registerPageType(key: string, config: ContextEngine.PageTypeConfig): void {
    this.pageTypes[key] = config;
  }

  clearCacheForUrl(url: string): void {
    cacheClear(this.storage, this.disableCache, url);
  }

  private _readCache(url: string): ContextEngine.ContextResult | null {
    return cacheRead(this.storage, this.disableCache, this.cacheTTL, url);
  }

  private _writeCache(url: string, value: ContextEngine.ContextResult): void {
    cacheWrite(
      this.storage,
      this.disableCache,
      this.cacheTTL,
      url,
      value,
      (message: string) => {
        this._log('context_cache_write_error', { message });
      }
    );
  }

  private _log(event: string, payload: unknown): void {
    try {
      if (this.analytics?.log) {
        this.analytics.log(event, payload);
      } else {
        console.debug('[ContextDetector]', event, payload);
      }
    } catch {
      // ignore logging errors
    }
  }

  private _matchByUrl(
    url: string,
    pageTypeConfig: ContextEngine.PageTypeConfig
  ): boolean {
    return matchByUrl(url, pageTypeConfig);
  }

  private _extractMetadata(doc: Document): ContextEngine.Metadata {
    return extractMetadata(doc);
  }

  private _extractKeywords(url: string, doc: Document): string[] {
    return extractKeywords(url, doc, this.baseOrigin);
  }

  async detect({
    url = window?.location?.href ?? '',
    doc = document,
  }: {
    url?: string;
    doc?: Document;
  } = {}): Promise<{
    context: ContextEngine.ContextResult;
    onMatch: (context: ContextEngine.ContextResult) => void;
  }> {
    const cached = this._readCache(url);
    if (cached) {
      this._log('context_cache_hit', { url });
      return { context: cached, onMatch: () => {} };
    }

    const detectionPromise: Promise<{
      context: ContextEngine.ContextResult;
      onMatch: (context: ContextEngine.ContextResult) => void;
    }> = new Promise(resolve => {
      const result: ContextEngine.ContextResult = {
        pageType: 'generic',
        confidence: 0,
        metadata: {},
        keywords: [],
        raw: { url },
      };

      try {
        // Score each page type by URL and DOM contributions
        let best: {
          key: string;
          cfg: ContextEngine.PageTypeConfig;
          score: number;
        } | null = null;
        for (const [key, cfg] of Object.entries(this.pageTypes)) {
          const urlMatched = url ? this._matchByUrl(url, cfg) : false;
          const selectors = cfg.domSelectors || [];
          const totalDom = selectors.length;
          let domMatches = 0;
          if (doc && totalDom > 0) {
            for (const sel of selectors) {
              if (doc.querySelector(sel)) domMatches++;
            }
          }

          let score = 0;
          if (urlMatched && totalDom === 0) {
            score = 1; // all confidence from URL when no DOM selectors
          } else if (urlMatched && totalDom > 0) {
            const urlPortion = 0.5;
            const domPortion = 0.5 * (domMatches / totalDom);
            score = urlPortion + domPortion;
          } else if (!urlMatched && totalDom > 0) {
            score = totalDom > 0 ? domMatches / totalDom : 0; // DOM-only
          }

          if (!best || score > best.score) {
            best = { key, cfg, score };
          }
        }

        if (best && best.score > 0) {
          result.pageType = best.key;
          result.confidence = Number(best.score.toFixed(3));
          if (best.cfg.extractors) {
            const extracted: Record<string, ContextEngine.ExtractedValue> = {};
            for (const [k, fn] of Object.entries(best.cfg.extractors)) {
              try {
                extracted[k] = fn(doc);
              } catch (error) {
                console.error(`Error extracting '${k}'`, error);
                extracted[k] = null;
              }
            }
            result.extracted = extracted;
          }
        } else {
          result.pageType = 'generic';
          result.confidence = 0;
        }

        result.metadata = this._extractMetadata(doc);
        result.keywords = this._extractKeywords(url, doc);
        result.detectedAt = new Date().toISOString();

        try {
          const h1 = doc.querySelector('h1');
          if (h1?.textContent) result.raw.h1 = h1.textContent.trim();
        } catch {
          // no-op
        }

        this._writeCache(url, result);
        this._log('context_detection_success', {
          url,
          pageType: result.pageType,
          confidence: result.confidence,
        });

        // Execute onMatch action with delay to ensure widget is ready
        setTimeout(() => {
          try {
            if (best?.cfg.onMatch) {
              best.cfg.onMatch(result);
            }
          } catch (error) {
            console.error('Error executing onMatch:', error);
          }
        }); // Small delay to ensure widget stores are ready

        resolve({ context: result, onMatch: best?.cfg.onMatch || (() => {}) });
      } catch (e: any) {
        this._log('context_detection_error', { message: e.message });
        resolve({
          context: {
            pageType: 'generic',
            confidence: 0,
            metadata: {},
            keywords: [],
            raw: { url },
          },
          onMatch: () => {},
        });
      }
    });

    try {
      return await Promise.race([
        detectionPromise,
        timeoutPromise(this.timeoutMs),
      ]);
    } catch (e: any) {
      this._log('context_detection_timeout_or_error', {
        message: e.message,
        url,
      });
      const fallback: {
        context: ContextEngine.ContextResult;
        onMatch: (context: ContextEngine.ContextResult) => void;
      } = {
        context: {
          pageType: 'generic',
          confidence: 0,
          metadata: {},
          keywords: [],
          raw: { url },
          detectedAt: new Date().toISOString(),
        },
        onMatch: () => {},
      };
      this._writeCache(url, fallback.context);
      return fallback;
    }
  }
}
